<template>
  <div class="ls-page" style="min-height: auto; padding: 0; background: transparent;">
    <div class="ls-wrap" style="width: 100%;">

      <header class="ls-hero" style="margin-bottom: 14px;">
        <div class="ls-hero-grid">
          <div class="ls-hero-main">
            <div class="ls-hero-title">ACCOUNTING & MANAGEMENT</div>
          </div>
        </div>
      </header>

      <b-tabs v-model="activeSubTab" content-class="inner-tab-content" class="ls-tabs">

        <!-- ================= TRANSFER ================= -->
        <b-tab title="Transfer Invoices">
          <!-- Filters -->
          <div class="ls-filterstrip" style="background:#fff; border-bottom:1px solid #E5E1DA; padding:16px;">
            <div class="d-flex flex-wrap align-items-end" style="gap:15px;">
              <div style="width: 320px;">
                <label class="small text-muted mb-1 font-weight-bold">Select Store (Ordering Location)</label>
                <b-form-select v-model="transferStoreId" :options="transferStoreOptions" size="sm" @input="onTransferStoreChange" />
              </div>
              
              <div style="width: 260px;">
                <label class="small text-muted mb-1 font-weight-bold">Date Range</label>
                <date-picker
                  v-model="transferDateRange"
                  range
                  format="MM-DD-YYYY"
                  valueType="format"
                  :clearable="true"
                  :editable="false"
                  placeholder="All dates"
                  style="width: 100%;"
                ></date-picker>
              </div>

              <div style="display: flex; gap: 6px; margin-bottom: 2px;">
                <b-button size="sm" variant="outline-secondary" @click="setTransferDate('this_week')">This Week</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setTransferDate('last_week')">Last Week</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setTransferDate('this_month')">This Month</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setTransferDate('last_month')">Last Month</b-button>
              </div>
            </div>
          </div>

          <div v-if="!transferStoreId" class="text-center p-5 text-muted">
            <b-icon icon="geo-alt" font-scale="2" class="mb-2"></b-icon>
            <p class="mb-0">Please select a <strong>Store</strong> above to view invoices.</p>
          </div>
          <div v-else-if="transferLoading" class="text-center p-5 text-muted">
            <b-spinner small></b-spinner> Loading transfer invoices…
          </div>
          <template v-else>
            <div class="stats-grid">
              <div class="stat-card stat-new">
                <div class="stat-label">NEW INVOICES</div>
                <div class="stat-value">${{ fmt(filteredTransferStats.total_new_amount) }}</div>
              </div>
              <div class="stat-card stat-delivered">
                <div class="stat-label">DELIVERED INVOICES</div>
                <div class="stat-value">${{ fmt(filteredTransferStats.total_delivered_amount) }}</div>
              </div>
              <div class="stat-card stat-completed">
                <div class="stat-label">COMPLETED INVOICES</div>
                <div class="stat-value">${{ fmt(filteredTransferStats.total_completed_amount) }}</div>
              </div>
              <div class="stat-card stat-due">
                <div class="stat-label">TOTAL DUE</div>
                <div class="stat-value">${{ fmt(filteredTransferStats.total_due_amount) }}</div>
              </div>
              <div class="stat-card stat-paid">
                <div class="stat-label">TOTAL PAID</div>
                <div class="stat-value">${{ fmt(filteredTransferStats.total_paid_amount) }}</div>
              </div>
            </div>

            <b-tabs v-model="transferInnerTab" content-class="inner-tab-content" class="ls-tabs-inner">
              <b-tab :title="`New Invoices (${filteredTransferBuckets.new.length})`">
                <transfer-table :orders="filteredTransferBuckets.new" :selectable="false" :get-location="getLocationName" />
              </b-tab>

              <b-tab :title="`Delivered Invoices (${filteredTransferBuckets.delivered.length})`">
                <transfer-table :orders="filteredTransferBuckets.delivered" :selectable="false" :get-location="getLocationName" />
              </b-tab>

              <b-tab :title="`Completed Invoices (${filteredTransferBuckets.completed.length})`">
                <div class="ls-filterstrip" style="background:#fff; border-bottom:1px solid #E5E1DA; padding:14px 16px;">
                  <div class="d-flex align-items-center">
                    <div class="text-muted" style="font-size:13px;">
                      Select unpaid completed transfers below, then click process.
                    </div>
                    <div class="ml-auto">
                      <b-button class="ls-btn-primary" @click="openProcessModal('transfer')" :disabled="selectedTransfers.length === 0">
                        <b-icon icon="cash-stack"></b-icon>
                        Process Selected ({{ selectedTransfers.length }}) — Due: ${{ fmt(getSelectedDue('transfer')) }}
                      </b-button>
                    </div>
                  </div>
                </div>
                <transfer-table
                  :orders="filteredTransferBuckets.completed"
                  :selectable="true"
                  :get-location="getLocationName"
                  v-model="selectedTransfers"
                  :all-selected.sync="allTransfersSelected"
                />
              </b-tab>
            </b-tabs>
          </template>
        </b-tab>

        <!-- ================= CUSTOMER ================= -->
        <b-tab title="Customer Invoices">
          <!-- Filters -->
          <div class="ls-filterstrip" style="background:#fff; border-bottom:1px solid #E5E1DA; padding:16px;">
            <div class="d-flex flex-wrap align-items-end" style="gap:15px;">
              <div style="width: 320px;">
                <label class="small text-muted mb-1 font-weight-bold">Select Store (To Location)</label>
                <b-form-select v-model="customerStoreId" :options="customerStoreOptions" size="sm" @input="onCustomerStoreChange" />
              </div>

              <div style="width: 260px;">
                <label class="small text-muted mb-1 font-weight-bold">Date Range</label>
                <date-picker
                  v-model="customerDateRange"
                  range
                  format="MM-DD-YYYY"
                  valueType="format"
                  :clearable="true"
                  :editable="false"
                  placeholder="All dates"
                  style="width: 100%;"
                ></date-picker>
              </div>

              <div style="display: flex; gap: 6px; margin-bottom: 2px;">
                <b-button size="sm" variant="outline-secondary" @click="setCustomerDate('this_week')">This Week</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setCustomerDate('last_week')">Last Week</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setCustomerDate('this_month')">This Month</b-button>
                <b-button size="sm" variant="outline-secondary" @click="setCustomerDate('last_month')">Last Month</b-button>
              </div>
            </div>
          </div>

          <div v-if="!customerStoreId" class="text-center p-5 text-muted">
            <b-icon icon="geo-alt" font-scale="2" class="mb-2"></b-icon>
            <p class="mb-0">Please select a <strong>Store</strong> above to view invoices.</p>
          </div>
          <div v-else-if="customerLoading" class="text-center p-5 text-muted">
            <b-spinner small></b-spinner> Loading customer invoices…
          </div>
          <template v-else>
            <div class="stats-grid">
              <div class="stat-card stat-new">
                <div class="stat-label">NEW INVOICES</div>
                <div class="stat-value">${{ fmt(filteredCustomerStats.total_new_amount) }}</div>
              </div>
              <div class="stat-card stat-delivered">
                <div class="stat-label">DELIVERED INVOICES</div>
                <div class="stat-value">${{ fmt(filteredCustomerStats.total_delivered_amount) }}</div>
              </div>
              <div class="stat-card stat-completed">
                <div class="stat-label">COMPLETED INVOICES</div>
                <div class="stat-value">${{ fmt(filteredCustomerStats.total_completed_amount) }}</div>
              </div>
              <div class="stat-card stat-due">
                <div class="stat-label">TOTAL DUE</div>
                <div class="stat-value">${{ fmt(filteredCustomerStats.total_due_amount) }}</div>
              </div>
              <div class="stat-card stat-paid">
                <div class="stat-label">TOTAL PAID</div>
                <div class="stat-value">${{ fmt(filteredCustomerStats.total_paid_amount) }}</div>
              </div>
            </div>

            <b-tabs v-model="customerInnerTab" content-class="inner-tab-content" class="ls-tabs-inner">
              <b-tab :title="`New Invoices (${filteredCustomerBuckets.new.length})`">
                <customer-table :invoices="filteredCustomerBuckets.new" :selectable="false" :get-location="getLocationName" />
              </b-tab>

              <b-tab :title="`Delivered Invoices (${filteredCustomerBuckets.delivered.length})`">
                <customer-table :invoices="filteredCustomerBuckets.delivered" :selectable="false" :get-location="getLocationName" />
              </b-tab>

              <b-tab :title="`Completed Invoices (${filteredCustomerBuckets.completed.length})`">
                <div class="ls-filterstrip" style="background:#fff; border-bottom:1px solid #E5E1DA; padding:14px 16px;">
                  <div class="d-flex align-items-center">
                    <div class="text-muted" style="font-size:13px;">
                      Select unpaid completed invoices below, then click process.
                    </div>
                    <div class="ml-auto">
                      <b-button class="ls-btn-primary" @click="openProcessModal('customer')" :disabled="selectedCustomers.length === 0">
                        <b-icon icon="cash-stack"></b-icon>
                        Process Selected ({{ selectedCustomers.length }}) — Due: ${{ fmt(getSelectedDue('customer')) }}
                      </b-button>
                    </div>
                  </div>
                </div>
                <customer-table
                  :invoices="filteredCustomerBuckets.completed"
                  :selectable="true"
                  :get-location="getLocationName"
                  v-model="selectedCustomers"
                  :all-selected.sync="allCustomersSelected"
                  :get-due="getCustomerDue"
                />
              </b-tab>
            </b-tabs>
          </template>
        </b-tab>
      </b-tabs>
    </div>

    <!-- ================= PROCESS PAYMENT MODAL ================= -->
    <b-modal
      id="process-payment-modal"
      hide-footer hide-header centered scrollable no-close-on-backdrop
      @shown="focusReferenceField"
      dialog-class="receipt-dialog" content-class="receipt-content"
    >
      <div class="pi-doc" style="display: block; position: relative;">
        <button class="close-btn" @click="$bvModal.hide('process-payment-modal')">×</button>

        <div class="pi-banner">
          <div class="pi-banner-invoice">
            <div class="pi-hero-label">SOLD TO</div>
            <div class="pi-hero-number" style="font-size:20px; color:#1E1B18; margin-bottom:4px;">
              {{ getLocationName(currentStoreLocation) }}
            </div>
            <div class="pi-sub" style="font-size:12px; line-height:1.4;">
              {{ getLocationAddress(currentStoreLocation) || 'Store Address' }}<br>
            </div>
          </div>
          <div class="pi-banner-brand">
            <div class="pi-brand-text">
              <div class="pi-brand-name">MECCA WHOLESALE LLC</div>
              <div class="pi-brand-line pi-brand-line--strong">101 E VENANGO STREET PHILADELPHIA, 19134</div>
              <div class="pi-brand-line">www.myfurnituremecca.com</div>
            </div>
          </div>
          <div class="pi-banner-qr" style="align-items: stretch; text-align:left; background:#fff; padding:14px;">
            <div class="pi-hero-meta" style="grid-template-columns:1fr; gap:12px; margin-top:0;">
              <div>
                <span style="font-size:10px; font-weight:700; text-transform:uppercase; color:#736C63;">Date</span>
                <b style="font-size:14px; display:block; margin-top:2px;">{{ todayDate }}</b>
              </div>
              <div>
                <span style="font-size:10px; font-weight:700; text-transform:uppercase; color:#736C63;">Reference #</span>
                <b-form-input
                  ref="refInput" v-model="paymentForm.reference" placeholder="Enter Ref #"
                  class="pi-ref-input"
                  :state="paymentForm.reference && paymentForm.reference.trim() !== '' ? true : null"
                ></b-form-input>
              </div>
            </div>
          </div>
        </div>

        <b-alert v-if="paymentError" show variant="danger" class="mt-3 mb-0">
          <b-icon icon="exclamation-triangle-fill"></b-icon> {{ paymentError }}
        </b-alert>

        <div class="pi-card">
          <h2 class="pi-title">Order details</h2>
          <div class="pi-plist-head">
            <span>Invoice #</span>
            <span>Item #</span>
            <span>Description</span>
            <span class="text-center">Qty</span>
            <span class="text-right">Price</span>
            <span class="text-right">Total</span>
          </div>
          <div class="pi-plist">
            <template v-for="inv in currentModalInvoices">
              <div v-if="!inv.items || inv.items.length === 0" :key="inv.id + '-empty'" class="pi-pcard">
                <div class="pi-pdesc"><div class="pi-kicker">{{ inv.ref }}</div></div>
                <div class="pi-pdesc"><div class="pi-pname">—</div></div>
                <div class="pi-pdesc"><div class="pi-pname">(No line items returned)</div></div>
                <div class="pi-pprice text-center"><b>—</b></div>
                <div class="pi-pdel text-right"><b>—</b></div>
                <div class="pi-ptotal text-right"><b class="pi-total-amt">${{ fmt(inv.due) }}</b></div>
              </div>
              <div v-for="(item, idx) in inv.items" :key="inv.id + '-' + idx" class="pi-pcard">
                <div class="pi-pdesc"><div class="pi-kicker" v-if="idx === 0">{{ inv.ref }}</div></div>
                <div class="pi-pdesc"><div class="pi-pname">{{ (item.product_sku || item.sku || '').toUpperCase() }}</div></div>
                <div class="pi-pdesc"><div class="pi-pname">{{ (item.product_name || item.name || '').toUpperCase() }}</div></div>
                <div class="pi-pprice text-center"><b>{{ item.quantity }}</b></div>
                <div class="pi-pdel text-right"><b>${{ fmt(item.unit_price || item.wholesale_price || item.price) }}</b></div>
                <div class="pi-ptotal text-right">
                  <b class="pi-total-amt">${{ fmt((item.quantity || 0) * (item.unit_price || item.wholesale_price || item.price || 0)) }}</b>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="pi-card pi-totals">
          <div class="pi-col">
            <h3>Payment Summary</h3>
            <div class="pi-row"><span>Total Amount Due</span><i></i><b>${{ fmt(currentModalDue) }}</b></div>
            <div class="pi-row"><span>Amount Paying</span><i></i>
              <b>
                <b-form-input
                  v-model.number="paymentForm.amount" type="number" step="0.01"
                  class="pi-amount-input"
                  :state="paymentForm.amount > 0 && paymentForm.amount <= currentModalDue ? true : null"
                ></b-form-input>
              </b>
            </div>
            <div class="pi-row"><span>Payment Type</span><i></i><b>CHECK / TRANSFER</b></div>
            
            <div class="pi-grand" style="margin-top:16px; border:none;">
              <span>TOTAL TO PAY:</span><span>${{ fmt(paymentForm.amount) }}</span>
            </div>

            <div style="margin-top:16px;">
              <label class="small text-muted mb-1 font-weight-bold">Comments</label>
              <b-form-textarea v-model="paymentForm.comments" placeholder="Optional notes…" rows="2" max-rows="4" size="sm" />
            </div>
            <div style="margin-top:12px; font-size:12px; color:#736C63;">
              <b-icon icon="info-circle"></b-icon> Payment receipt # will be generated automatically after submission.
            </div>
          </div>
          
          <div class="pi-col" style="display:flex; flex-direction:column; justify-content:flex-end; padding-left: 20px; border-left: 1px solid #E2DED8;">
            <b-button variant="outline-secondary" @click="$bvModal.hide('process-payment-modal')" style="height:44px; font-weight:800; font-size:14px;">CANCEL</b-button>
            <b-button variant="dark" @click="submitPayment" :disabled="isSubmitting || !paymentForm.amount || !paymentForm.reference" style="height:44px; font-weight:800; font-size:14px; margin-top:12px;">
              <b-spinner small v-if="isSubmitting"></b-spinner>
              <b-icon v-else icon="check2-circle"></b-icon>
              {{ isSubmitting ? 'PROCESSING…' : 'SUBMIT PAYMENT' }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

const TransferTable = {
  name: 'TransferTable',
  props: {
    orders:      { type: Array,   required: true },
    selectable:  { type: Boolean, default: false },
    getLocation: { type: Function, required: true },
    value:       { type: Array,   default: () => [] },
    allSelected: { type: Boolean, default: false },
  },
  computed: {
    selectedIds() { return this.selectable ? this.value.map(String) : []; },
  },
  methods: {
    fmt(v) { return (parseFloat(v) || 0).toFixed(2); },
    isWarehouse(id) { return ['0','40','41','42'].includes(String(id)); },
    toggleAll(checked) {
      this.$emit('update:allSelected', checked);
      this.$emit('input', checked ? this.orders.filter(o => (o.amount - o.paid) > 0).map(o => String(o.id)) : []);
    },
    setSelected(id, checked) {
      const next = new Set(this.selectedIds);
      if (checked) next.add(String(id)); else next.delete(String(id));
      this.$emit('input', Array.from(next));
    },
    isChecked(id) { return this.selectedIds.includes(String(id)); },
  },
  template: `
    <div class="ls-card ls-table-card" style="margin-bottom:0; border:none; box-shadow:none;">
      <div class="ls-table-scroll">
        <table class="ls-table">
          <thead>
            <tr>
              <th v-if="selectable" class="ls-th" style="width:40px;">
                <b-form-checkbox :checked="allSelected" @change="toggleAll"
                  :disabled="orders.filter(o => (o.amount - o.paid) > 0).length === 0" />
              </th>
              <th class="ls-th">Order #</th>
              <th class="ls-th">Date</th>
              <th class="ls-th">From (Source)</th>
              <th class="ls-th">To (Store)</th>
              <th class="ls-th text-center">Items</th>
              <th class="ls-th text-right">Total</th>
              <th class="ls-th text-right">Paid</th>
              <th class="ls-th text-right">Due</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="orders.length === 0">
              <td :colspan="selectable ? 9 : 8" class="text-center p-5 text-muted">No orders in this bucket.</td>
            </tr>
            <tr v-for="order in orders" :key="order.id" class="ls-row">
              <td v-if="selectable" class="ls-td">
                <b-form-checkbox :checked="isChecked(order.id)"
                  :disabled="(order.amount - order.paid) <= 0"
                  @change="setSelected(order.id, $event)" />
              </td>
              <td class="ls-td"><strong>TO-{{ order.id }}</strong></td>
              <td class="ls-td">{{ order.ts }}</td>
              <td class="ls-td"><span :class="isWarehouse(order.selling_location) ? 'fw-bold text-dark' : ''">{{ getLocation(order.selling_location) }}</span></td>
              <td class="ls-td"><strong>{{ getLocation(order.ordering_location) }}</strong></td>
              <td class="ls-td text-center"><b-badge variant="light">{{ order.items ? order.items.length : 0 }}</b-badge></td>
              <td class="ls-td text-right">\${{ fmt(order.amount) }}</td>
              <td class="ls-td text-right text-success">\${{ fmt(order.paid) }}</td>
              <td class="ls-td text-right">
                <span v-if="(order.amount - order.paid) > 0" class="text-danger font-weight-bold">\${{ fmt(order.amount - order.paid) }}</span>
                <span v-else-if="(order.amount - order.paid) < 0" class="text-success font-weight-bold"><b-badge variant="success">OVERPAID</b-badge></span>
                <span v-else class="text-muted">\$0.00</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `,
};

const CustomerTable = {
  name: 'CustomerTable',
  props: {
    invoices:    { type: Array,   required: true },
    selectable:  { type: Boolean, default: false },
    getLocation: { type: Function, required: true },
    value:       { type: Array,   default: () => [] },
    allSelected: { type: Boolean, default: false },
    getDue:      { type: Function, default: null },
  },
  computed: {
    selectedIds() { return this.selectable ? this.value.map(String) : []; },
    due() { return this.getDue || ((inv) => Math.max(0, parseFloat(inv.order_total || 0) - parseFloat(inv.paid || 0))); },
  },
  methods: {
    fmt(v) { return (parseFloat(v) || 0).toFixed(2); },
    toggleAll(checked) {
      this.$emit('update:allSelected', checked);
      this.$emit('input', checked ? this.invoices.filter(o => this.due(o) > 0).map(o => String(o.invoice_id)) : []);
    },
    setSelected(id, checked) {
      const next = new Set(this.selectedIds);
      if (checked) next.add(String(id)); else next.delete(String(id));
      this.$emit('input', Array.from(next));
    },
    isChecked(id) { return this.selectedIds.includes(String(id)); },
  },
  template: `
    <div class="ls-card ls-table-card" style="margin-bottom:0; border:none; box-shadow:none;">
      <div class="ls-table-scroll">
        <table class="ls-table">
          <thead>
            <tr>
              <th v-if="selectable" class="ls-th" style="width:40px;">
                <b-form-checkbox :checked="allSelected" @change="toggleAll"
                  :disabled="invoices.filter(o => due(o) > 0).length === 0" />
              </th>
              <th class="ls-th">Invoice #</th>
              <th class="ls-th">Date</th>
              <th class="ls-th">Created By</th>
              <th class="ls-th">Customer</th>
              <th class="ls-th">To Location</th>
              <th class="ls-th text-center">Items</th>
              <th class="ls-th text-right">Total</th>
              <th class="ls-th text-right">Paid</th>
              <th class="ls-th text-right">Due</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="invoices.length === 0">
              <td :colspan="selectable ? 10 : 9" class="text-center p-5 text-muted">No invoices in this bucket.</td>
            </tr>
            <tr v-for="inv in invoices" :key="inv.invoice_id" class="ls-row">
              <td v-if="selectable" class="ls-td">
                <b-form-checkbox :checked="isChecked(inv.invoice_id)"
                  :disabled="due(inv) <= 0"
                  @change="setSelected(inv.invoice_id, $event)" />
              </td>
              <td class="ls-td"><strong>{{ inv.invoice_id }}</strong></td>
              <td class="ls-td">{{ inv.ts }}</td>
              <td class="ls-td">{{ inv.created_by || '—' }}</td>
              <td class="ls-td">{{ inv.fullname }}</td>
              <td class="ls-td"><strong>{{ getLocation(inv.location_id) }}</strong></td>
              <td class="ls-td text-center"><b-badge variant="light">{{ inv.item_count || 0 }}</b-badge></td>
              <td class="ls-td text-right">\${{ fmt(inv.order_total) }}</td>
              <td class="ls-td text-right text-success">\${{ fmt(inv.paid) }}</td>
              <td class="ls-td text-right">
                <span v-if="due(inv) > 0" class="text-danger font-weight-bold">\${{ fmt(due(inv)) }}</span>
                <span v-else class="text-muted">\$0.00</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `,
};

export default {
  name: "AccountingInvoices",
  components: { TransferTable, CustomerTable, DatePicker },
  data() {
    return {
      activeSubTab: 0,
      packet: {},
      
      // Transfer state
      transferStoreId: null,
      transferDateRange: null,
      transferLoading: false,
      transferBuckets: { new: [], delivered: [], completed: [] },
      transferStats: {},
      transferInnerTab: 2,
      selectedTransfers: [],
      allTransfersSelected: false,

      // Customer state
      customerStoreId: null,
      customerDateRange: null,
      customerLoading: false,
      customerBuckets: { new: [], delivered: [], completed: [] },
      customerStats: {},
      customerInnerTab: 2,
      selectedCustomers: [],
      allCustomersSelected: false,

      // Modal state
      currentModalType: null,
      currentModalDue: 0,
      currentModalInvoices: [],
      paymentForm: { amount: null, reference: '', comments: '' },
      isSubmitting: false,
      paymentError: null,
    };
  },
  computed: {
    todayDate() { return moment().format('MM/DD/YYYY'); },
    transferStoreOptions() {
      const locs = this.packet.locations || [];
      const options = [{ value: null, text: '-- Select a Store --' }];
      locs.filter(l => !['0','40','41','42','47','46'].includes(String(l.value)))
          .forEach(l => options.push({ value: l.value, text: l.text }));
      return options;
    },
    customerStoreOptions() { return this.transferStoreOptions; },
    currentStoreLocation() {
      if (this.currentModalType === 'transfer') return this.transferStoreId || 'N/A';
      if (this.currentModalType === 'customer') return this.customerStoreId || 'N/A';
      return 'N/A';
    },

    /* ================= DATE FILTERING & STATS ================= */
    filteredTransferBuckets() {
      const filterByDate = (list) => {
        if (!this.transferDateRange || this.transferDateRange.length !== 2) return list;
        const start = moment(this.transferDateRange[0], 'MM-DD-YYYY').startOf('day');
        const end = moment(this.transferDateRange[1], 'MM-DD-YYYY').endOf('day');
        return list.filter(o => {
          const d = moment(o.ts, 'MM-DD-YYYY HH:mm:ss A');
          return d.isValid() && d.isBetween(start, end, null, '[]');
        });
      };
      return {
        new: filterByDate(this.transferBuckets.new),
        delivered: filterByDate(this.transferBuckets.delivered),
        completed: filterByDate(this.transferBuckets.completed),
      };
    },
    filteredTransferStats() {
      const b = this.filteredTransferBuckets;
      const sum = (arr, key) => arr.reduce((s, o) => s + (parseFloat(o[key]) || 0), 0);
      
      return {
        total_new_amount: sum(b.new, 'amount'),
        total_delivered_amount: sum(b.delivered, 'amount'),
        total_completed_amount: sum(b.completed, 'amount'),
        total_due_amount: b.completed.reduce((s, o) => s + Math.max(0, (parseFloat(o.amount)||0) - (parseFloat(o.paid)||0)), 0),
        total_paid_amount: [...b.new, ...b.delivered, ...b.completed].reduce((s, o) => s + (parseFloat(o.paid) || 0), 0),
      };
    },
    filteredCustomerBuckets() {
      const filterByDate = (list) => {
        if (!this.customerDateRange || this.customerDateRange.length !== 2) return list;
        const start = moment(this.customerDateRange[0], 'MM-DD-YYYY').startOf('day');
        const end = moment(this.customerDateRange[1], 'MM-DD-YYYY').endOf('day');
        return list.filter(o => {
          const d = moment(o.ts, 'MM-DD-YYYY HH:mm:ss A');
          return d.isValid() && d.isBetween(start, end, null, '[]');
        });
      };
      return {
        new: filterByDate(this.customerBuckets.new),
        delivered: filterByDate(this.customerBuckets.delivered),
        completed: filterByDate(this.customerBuckets.completed),
      };
    },
    filteredCustomerStats() {
      const b = this.filteredCustomerBuckets;
      const sum = (arr, key) => arr.reduce((s, o) => s + (parseFloat(o[key]) || 0), 0);
      
      return {
        total_new_amount: sum(b.new, 'order_total'),
        total_delivered_amount: sum(b.delivered, 'order_total'),
        total_completed_amount: sum(b.completed, 'order_total'),
        total_due_amount: b.completed.reduce((s, o) => s + this.getCustomerDue(o), 0),
        total_paid_amount: [...b.new, ...b.delivered, ...b.completed].reduce((s, o) => s + (parseFloat(o.paid) || 0), 0),
      };
    }
  },
  watch: {
    selectedTransfers() {
      const selectable = this.filteredTransferBuckets.completed.filter(o => (o.amount - o.paid) > 0).length;
      this.allTransfersSelected = this.selectedTransfers.length > 0 && this.selectedTransfers.length === selectable;
    },
    selectedCustomers() {
      const selectable = this.filteredCustomerBuckets.completed.filter(o => this.getCustomerDue(o) > 0).length;
      this.allCustomersSelected = this.selectedCustomers.length > 0 && this.selectedCustomers.length === selectable;
    },
  },
  created() {
    this.packet = JSON.parse(localStorage.packet || '{}');
    this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
  },
  methods: {
    fmt(v) { return (parseFloat(v) || 0).toFixed(2); },
    getLocationName(locId) {
      if (locId === null || locId === undefined || locId === '') return '—';
      const idStr = String(locId).trim();
      if (['0','40','41','42'].includes(idStr)) return 'MECCA WHOLESALE';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === idStr);
      return found ? found.text : `Location #${locId}`;
    },
    getLocationAddress(locId) {
      if (locId === null || locId === undefined || locId === '') return '';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === String(locId));
      return found ? (found.address || '') : '';
    },
    getCustomerDue(inv) {
      return Math.max(0, parseFloat(inv.order_total || 0) - parseFloat(inv.paid || 0));
    },

    /* ================= QUICK DATE FILTERS ================= */
    setTransferDate(type) {
      const now = moment();
      if (type === 'this_week') {
        this.transferDateRange = [now.clone().startOf('isoWeek').format('MM-DD-YYYY'), now.clone().endOf('isoWeek').format('MM-DD-YYYY')];
      } else if (type === 'last_week') {
        this.transferDateRange = [now.clone().subtract(1, 'week').startOf('isoWeek').format('MM-DD-YYYY'), now.clone().subtract(1, 'week').endOf('isoWeek').format('MM-DD-YYYY')];
      } else if (type === 'this_month') {
        this.transferDateRange = [now.clone().startOf('month').format('MM-DD-YYYY'), now.clone().endOf('month').format('MM-DD-YYYY')];
      } else if (type === 'last_month') {
        this.transferDateRange = [now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY'), now.clone().subtract(1, 'month').endOf('month').format('MM-DD-YYYY')];
      }
    },
    setCustomerDate(type) {
      const now = moment();
      if (type === 'this_week') {
        this.customerDateRange = [now.clone().startOf('isoWeek').format('MM-DD-YYYY'), now.clone().endOf('isoWeek').format('MM-DD-YYYY')];
      } else if (type === 'last_week') {
        this.customerDateRange = [now.clone().subtract(1, 'week').startOf('isoWeek').format('MM-DD-YYYY'), now.clone().subtract(1, 'week').endOf('isoWeek').format('MM-DD-YYYY')];
      } else if (type === 'this_month') {
        this.customerDateRange = [now.clone().startOf('month').format('MM-DD-YYYY'), now.clone().endOf('month').format('MM-DD-YYYY')];
      } else if (type === 'last_month') {
        this.customerDateRange = [now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY'), now.clone().subtract(1, 'month').endOf('month').format('MM-DD-YYYY')];
      }
    },

    /* ================= FETCHING ================= */
    async onTransferStoreChange() {
      this.selectedTransfers = [];
      this.allTransfersSelected = false;
      this.transferBuckets = { new: [], delivered: [], completed: [] };
      this.transferStats = {};
      if (!this.transferStoreId) return;
      await this.fetchTransfers();
    },
    async onCustomerStoreChange() {
      this.selectedCustomers = [];
      this.allCustomersSelected = false;
      this.customerBuckets = { new: [], delivered: [], completed: [] };
      this.customerStats = {};
      if (!this.customerStoreId) return;
      await this.fetchCustomers();
    },
    async fetchTransfers() {
      this.transferLoading = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP("/api/sales/get_store_transfer_orders/", { location_id: this.transferStoreId }, this, resolve, reject);
        });
        if (res.data && res.data.result) {
          const r = res.data.result;
          this.transferBuckets = {
            new:       r.new       || [],
            delivered: r.delivered || [],
            completed: r.completed || [],
          };
          this.transferStats = r.stats || {};
        }
      } catch (e) { console.error(e); }
      this.transferLoading = false;
    },
    async fetchCustomers() {
      this.customerLoading = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP("/api/sales/get_accounting_customer_invoices/", { location_id: this.customerStoreId }, this, resolve, reject);
        });
        if (res.data && res.data.result) {
          const r = res.data.result;
          this.customerBuckets = {
            new:       r.new       || [],
            delivered: r.delivered || [],
            completed: r.completed || [],
          };
          this.customerStats = r.stats || {};
        }
      } catch (e) { console.error(e); }
      this.customerLoading = false;
    },
    
    /* ================= PAYMENT ================= */
    getSelectedDue(type) {
      if (type === 'transfer') {
        return this.filteredTransferBuckets.completed
          .filter(o => this.selectedTransfers.map(String).includes(String(o.id)))
          .reduce((s, o) => s + Math.max(0, o.amount - o.paid), 0);
      }
      return this.filteredCustomerBuckets.completed
        .filter(o => this.selectedCustomers.map(String).includes(String(o.invoice_id)))
        .reduce((s, o) => s + this.getCustomerDue(o), 0);
    },
    openProcessModal(type) {
      const ids = (type === 'transfer' ? this.selectedTransfers : this.selectedCustomers).map(String);
      if (ids.length === 0) return;

      const list = type === 'transfer' ? this.filteredTransferBuckets.completed : this.filteredCustomerBuckets.completed;
      const storeIds = new Set();
      list.filter(o => ids.includes(String(type === 'transfer' ? o.id : o.invoice_id)))
          .forEach(o => storeIds.add(String(type === 'transfer' ? o.ordering_location : o.location_id)));
      
      if (storeIds.size > 1) {
        this.$bvToast.toast("Cannot process payments for multiple store locations in one transaction.", { variant: 'warning', autoHideDelay: 4000 });
        return;
      }

      this.currentModalType = type;
      this.currentModalDue = this.getSelectedDue(type);
      this.paymentForm.amount = this.currentModalDue;
      this.paymentForm.reference = '';
      this.paymentForm.comments = '';
      this.paymentError = null;

      const parseItems = (raw) => {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string') {
          try { return JSON.parse(raw); } catch (e) { return []; }
        }
        return [];
      };

      if (type === 'transfer') {
        this.currentModalInvoices = this.filteredTransferBuckets.completed
          .filter(o => ids.includes(String(o.id)))
          .map(o => ({ 
            id: o.id, 
            ref: `TO-${o.id}`, 
            due: o.amount - o.paid, 
            items: parseItems(o.items)
          }));
      } else {
        this.currentModalInvoices = this.filteredCustomerBuckets.completed
          .filter(o => ids.includes(String(o.invoice_id)))
          .map(o => ({ 
            id: o.invoice_id, 
            ref: o.invoice_id, 
            due: this.getCustomerDue(o), 
            items: parseItems(o.items)
          }));
      }
      this.$bvModal.show('process-payment-modal');
    },
    focusReferenceField() {
      this.$nextTick(() => { if (this.$refs.refInput) this.$refs.refInput.focus(); });
    },
    async submitPayment() {
      this.paymentError = null;
      if (!this.paymentForm.amount || this.paymentForm.amount <= 0) { this.paymentError = "Please enter a valid amount."; return; }
      if (this.paymentForm.amount > this.currentModalDue) { this.paymentError = `Amount cannot exceed $${this.fmt(this.currentModalDue)}.`; return; }
      if (!this.paymentForm.reference || this.paymentForm.reference.trim() === '') { this.paymentError = "Please enter a Reference Number."; return; }

      this.isSubmitting = true;
      const payload = {
        entity_type: this.currentModalType,
        invoices: this.currentModalInvoices,
        amount: this.paymentForm.amount,
        reference: this.paymentForm.reference.trim(),
        comments: (this.paymentForm.comments || '').trim(),
        processed_by: this.packet.userDetails.username || 'admin',
        to_location_id: this.currentStoreLocation,
        from_location_id: 0,
      };
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.postAsyncP("/api/sales/process_accounting_payment/", payload, this, resolve, reject);
        });
        if (res.data && res.data.error) {
          this.paymentError = res.data.error;
        } else {
          const receipt = res.data && res.data.result && res.data.result[0] ? res.data.result[0].payment_receipt : null;
          this.$bvToast.toast(receipt ? `Payment processed. Receipt #: ${receipt}` : "Payment processed successfully.", { variant: 'success', autoHideDelay: 5000 });
          this.$bvModal.hide('process-payment-modal');
          this.selectedTransfers = [];
          this.selectedCustomers = [];
          if (this.currentModalType === 'transfer' && this.transferStoreId) this.fetchTransfers();
          if (this.currentModalType === 'customer' && this.customerStoreId) this.fetchCustomers();
        }
      } catch (e) {
        this.paymentError = "Network error.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ---------------- PAGE SHELL ---------------- */
.ls-page { background: #F6F4F0; font-family: 'Manrope', system-ui, sans-serif; color: #221F1C; font-size: 17px; }
.ls-hero { background: #221F1C; color: #fff; border-radius: 20px; overflow: hidden; border-bottom: 5px solid #EE7A22; }
.ls-hero-grid { padding: 5px 30px; }
.ls-hero-title { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 700; margin: 4px 0 12px; display: flex; align-items: center; gap: 14px; }
.ls-btn-primary.btn { background: #EE7A22 !important; border-color: #EE7A22 !important; color: #fff !important; border-radius: 10px !important; font-weight: 700; padding: 10px 18px; }
.ls-card { background: #fff; border: 1px solid #E5E1DA; border-radius: 16px; }
.ls-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.ls-th { background: #221F1C; color: #fff; text-align: left; font-size: 15px; font-weight: 700; text-transform: uppercase; padding: 18px 14px; border-bottom: 3px solid #EE7A22; }
.ls-td { padding: 16px 14px; border-bottom: 1px solid #F1EEE9; }
.ls-row:hover td { background: #FAF9F7; }

/* ---------------- STATS CARDS ---------------- */
.stats-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; padding: 16px; background: #FAF9F7; border-bottom: 1px solid #E5E1DA; }
.stat-card { border-radius: 14px; padding: 14px 16px; border: 1.5px solid #E5E1DA; background: #fff; }
.stat-label { font-size: 11px; font-weight: 800; letter-spacing: 0.08em; color: #736C63; text-transform: uppercase; margin-bottom: 6px; }
.stat-value { font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 800; color: #221F1C; }
.stat-new { border-color: #F1C40F; background: #FFFCF1; }
.stat-new .stat-value { color: #B7950B; }
.stat-delivered { border-color: #3498DB; background: #F2F9FF; }
.stat-delivered .stat-value { color: #1F618D; }
.stat-completed { border-color: #2ECC71; background: #F1FCF4; }
.stat-completed .stat-value { color: #1E8449; }
.stat-due { border-color: #E74C3C; background: #FFF5F4; }
.stat-due .stat-value { color: #B03A2E; }
.stat-paid { border-color: #16A085; background: #F0FBF8; }
.stat-paid .stat-value { color: #0E6655; }

@media (max-width: 1200px) { .stats-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 700px)  { .stats-grid { grid-template-columns: repeat(2, 1fr); } }

/* ---------------- MODAL RECEIPT STYLES ---------------- */
.receipt-content { border: none; border-radius: 8px; box-shadow: 0 20px 60px rgba(0,0,0,0.35); overflow: hidden; background: #fff; }
.close-btn { position: absolute; top: 15px; right: 20px; background: none; border: none; font-size: 28px; color: #999; cursor: pointer; z-index: 10; }
.close-btn:hover { color: #000; }
.pi-doc { font-family: 'Manrope', Arial, sans-serif; color: #1E1B18; background: #fff; padding: 24px; }

.pi-banner { position: relative; display: grid; grid-template-columns: 1fr 1.2fr 1fr; align-items: stretch; gap: 18px; padding: 16px; border-radius: 12px; background: #FFF; border: 1.5px solid #E2DED8; border-bottom: 4px solid #EE7A22; margin-bottom: 24px; }
.pi-banner-invoice { padding-right: 10px; border-right: 1px solid #E2DED8; }
.pi-hero-label { font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #736C63; }
.pi-hero-number { font-family: 'Barlow Condensed', sans-serif; font-size: 26px; font-weight: 600; color: #D4620F; }
.pi-hero-meta { display: grid; gap: 10px; margin-top: 0; }
.pi-hero-meta span { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #736C63; }
.pi-hero-meta b { font-size: 14px; display: block; margin-top: 2px; }
.pi-ref-input { border: none; border-bottom: 1px solid #000; border-radius: 0; padding: 0; height: 22px; font-size: 14px; font-weight: 700; width: 100%; box-shadow: none !important; background: transparent; }
.pi-ref-input:focus { border-bottom: 2px solid #000; }
.pi-banner-brand { text-align: center; display: flex; align-items: center; justify-content: center; border-right: 1px solid #E2DED8; }
.pi-brand-name { font-family: 'Barlow Condensed', sans-serif; font-size: 24px; font-weight: 800; color: #1E1B18; margin-bottom: 4px; }
.pi-brand-line { font-size: 13px; color: #736C63; }
.pi-brand-line--strong { color: #1E1B18; font-weight: 700; }
.pi-banner-qr { display: flex; flex-direction: column; justify-content: center; background: #F6F4F0; border: 1px solid #E2DED8; border-radius: 8px; padding: 12px; }

.pi-card { background: #F4F2EF; border: 1px solid #E2DED8; border-radius: 12px; padding: 16px; margin-bottom: 20px; }
.pi-title { font-family: 'Barlow Condensed', sans-serif; font-size: 18px; font-weight: 600; text-transform: uppercase; margin: 0 0 12px; padding-left: 10px; border-left: 4px solid #EE7A22; line-height: 1; }

.pi-plist-head, .pi-pcard { 
  display: grid; 
  grid-template-columns: 90px 100px 1fr 60px 80px 90px; 
  gap: 12px; 
  align-items: center; 
}
.pi-plist-head { background: #1E1B18; color: #fff; border-radius: 6px; padding: 10px 14px; font-size: 11px; font-weight: 800; text-transform: uppercase; margin-bottom: 8px; }
.pi-pcard { background: #fff; border: 1px solid #E2DED8; border-radius: 6px; padding: 10px 14px; margin-bottom: 6px; }
.pi-pname { font-size: 13px; font-weight: 800; color: #1E1B18; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pi-total-amt { font-size: 14px; font-weight: 800; color: #D4620F; }

.pi-totals { display: grid; grid-template-columns: 1fr 1fr; padding: 20px; background: #F4F2EF; gap: 30px; }
.pi-col h3 { font-family: 'Barlow Condensed', sans-serif; font-size: 20px; font-weight: 800; text-transform: uppercase; margin: 0 0 16px; padding-bottom: 6px; border-bottom: 2px solid #EE7A22; }
.pi-row { display: flex; align-items: baseline; gap: 8px; font-size: 14px; padding: 6px 0; }
.pi-row span { font-weight: 600; color: #736C63; }
.pi-row i { flex: 1; border-bottom: 1px dotted #A9A29A; }
.pi-row b { font-weight: 800; }
.pi-amount-input { background-color: #ffffff; border: 1px solid #E2DED8; border-radius: 6px; padding: 0 8px; height: 32px; width: 120px; text-align: right; font-size: 15px; font-weight: 800; box-shadow: none !important; }
.pi-grand { display: flex; justify-content: space-between; align-items: center; background: #EE7A22; color: #fff; border-radius: 8px; padding: 12px 16px; font-weight: 800; font-size: 15px; }

@media (max-width: 767px) {
  .pi-banner { grid-template-columns: 1fr; text-align: center; gap: 12px; }
  .pi-banner-invoice { border-right: none; border-bottom: 1px solid #E2DED8; padding-bottom: 10px; padding-right: 0; }
  .pi-banner-brand { border-right: none; border-bottom: 1px solid #E2DED8; padding-bottom: 10px; }
  .pi-totals { grid-template-columns: 1fr; }
  .pi-plist-head, .pi-pcard { grid-template-columns: 1fr 1fr; gap: 8px; }
  .pi-plist-head span:nth-child(n+3), .pi-pcard div:nth-child(n+3) { display: none; }
}

/* Datepicker Override */
:deep(.mx-datepicker) { width: 100%; }
:deep(.mx-input) { height: 34px !important; font-size: 14px !important; border-radius: 8px !important; border: 1.5px solid #E5E1DA !important; text-align: left; }
</style>

<!-- CRITICAL: Unscoped tab overrides to fix BootstrapVue nesting bugs -->
<style>
/* ---------------- OUTER TABS ---------------- */
.ls-tabs > .nav-tabs {
  border-bottom: 2px solid #E5E1DA;
  padding-left: 10px;
  margin-bottom: 0;
}
.ls-tabs > .nav-tabs .nav-link {
  font-weight: 700;
  color: #736C63;
  border: 1.5px solid #E5E1DA;
  border-bottom: none;
  background: #F7F5F2;
  padding: 12px 24px;
  margin-right: 4px;
  border-radius: 8px 8px 0 0;
}
.ls-tabs > .nav-tabs .nav-link.active {
  background: #221F1C;
  color: #fff;
  border-color: #221F1C;
}
.ls-tabs > .tab-content {
  background: #fff;
  border: 1.5px solid #E5E1DA;
  border-top: none;
  border-radius: 0 0 12px 12px;
  padding: 0;
}

/* ---------------- INNER TABS ---------------- */
.ls-tabs-inner > .nav-tabs {
  padding: 12px 16px 0 !important;
  background: #FAF9F7 !important;
  border-bottom: 1px solid #E5E1DA !important;
  border-top: none !important;
  margin-bottom: 0 !important;
}
.ls-tabs-inner > .nav-tabs .nav-link {
  font-weight: 700;
  color: #736C63;
  border: 1.5px solid transparent;
  padding: 9px 20px;
  border-radius: 8px 8px 0 0 !important;
  margin-bottom: -1px;
}
.ls-tabs-inner > .nav-tabs .nav-link:hover {
  border-color: transparent;
  color: #221F1C;
}
.ls-tabs-inner > .nav-tabs .nav-link.active {
  background: #221F1C !important;
  color: #fff !important;
  border-color: #221F1C !important;
}
.ls-tabs-inner > .tab-content {
  padding: 0;
  background: #fff;
}
.ls-tabs-inner > .tab-content > .tab-pane {
  padding: 0;
}

/* ---------------- TABLE STYLES (Fallback) ---------------- */
.ls-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.ls-th { background: #221F1C; color: #fff; text-align: left; font-size: 15px; font-weight: 700; text-transform: uppercase; padding: 18px 14px; border-bottom: 3px solid #EE7A22; border-top: none; }
.ls-td { padding: 16px 14px; border-bottom: 1px solid #F1EEE9; }
.ls-row:hover td { background: #FAF9F7; }

/* ---------------- DIALOG OVERRIDES ---------------- */
.receipt-dialog { max-width: 1000px !important; width: 90% !important; margin: 1.75rem auto !important; }
@media (max-width: 767px) { .receipt-dialog { width: 100% !important; margin: 0 !important; } }
</style>


// WEBPACK FOOTER //
// src/components/Accounting/AccountingInvoices.vue